import { useState, useMemo, useEffect } from "react";
import { getStorageURL } from "../../firebase";
import { 
    Brain, Image as ImageIcon, CheckCircle, AlertCircle, 
    Sparkles, Quote, ListChecks, Target, Lightbulb, 
    TrendingUp, ThumbsUp, Users, MessageSquare, Camera, X,
    Copy, TrendingDown, Clock, Palette, Zap, Activity,
    AlertTriangle, Layout, MapPin, BarChart2,
    // Added Missing Imports for Modal
    ChevronLeft, ChevronRight, Heart, Aperture, Maximize, 
    Focus, Grid, Eye, Fingerprint, Coins, BarChart4,
    // NEW IMPORTS ADDED
    Compass, Shield, Share2, Star, Award, BrainCircuit, Landmark,
    // ADDITIONAL REQUIRED IMPORTS
    BookOpen
} from "lucide-react";

// --- Utility: Title Case / Format Text ---
export const toTitleCase = (str) => {
    if (!str) return '';
    return str.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// Alias for compatibility with new Modal code
export const formatText = toTitleCase;

// --- Utility: Modern Progress Bar ---
export const ModernProgressBar = ({ label, score, colorClass = "bg-accent-500", showValue = true }) => {
    const safeScore = Math.min(Math.max(score || 0, 0), 100);
    return (
        <div className="w-full group">
            <div className="flex justify-between items-end text-xs mb-1.5">
                <span className="text-slate-400 font-medium group-hover:text-white transition-colors">{label}</span>
                {showValue && <span className="font-mono text-white font-bold">{safeScore.toFixed(0)}%</span>}
            </div>
            <div className="w-full bg-slate-700/30 rounded-full h-1.5 overflow-hidden border border-white/5">
                <div 
                    className={`h-full rounded-full shadow-[0_0_10px_rgba(0,0,0,0.3)] transition-all duration-1000 ease-out ${colorClass}`} 
                    style={{ width: `${safeScore}%` }} 
                />
            </div>
        </div>
    );
};

// --- Utility: Score Pill ---
export const ScorePill = ({ label, value, icon: Icon, color = "text-slate-300" }) => (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-lg p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
            {Icon && <Icon size={14} className={color} />}
            <span className="text-xs text-slate-400 font-medium">{label}</span>
        </div>
        <span className={`text-sm font-bold font-mono ${color}`}>{value}</span>
    </div>
);

// --- Utility: Metric Card with Trend ---
export const MetricCard = ({ label, value, subtitle, icon: Icon, color = "text-cyan-400", trend }) => (
    <div className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-slate-700/50 p-4 hover:border-slate-600 transition-all">
        <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
                {Icon && <Icon size={16} className={color} />}
                <span className="text-xs text-slate-400 font-medium">{label}</span>
            </div>
            {trend && (
                <span className={`text-xs font-bold ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
                </span>
            )}
        </div>
        <div className={`text-2xl font-black ${color} font-mono mb-1`}>{value}</div>
        {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
    </div>
);

// --- Utility: Distribution Chart ---
export const DistributionBar = ({ data, colorMap }) => (
    <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => {
            const percentage = typeof value === 'number' ? value * 100 : value;
            const color = colorMap?.[key] || 'bg-cyan-500';
            return (
                <div key={key}>
                    <div className="flex justify-between text-xs mb-1">
                        <span className="text-slate-400">{formatText(key)}</span>
                        <span className="font-mono text-white font-bold">{percentage.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 w-full bg-slate-700/30 rounded-full overflow-hidden">
                        <div 
                            className={`h-full ${color} transition-all duration-1000`}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                </div>
            );
        })}
    </div>
);

// --- 1. Top-Level Summary Components (The Profile) ---

export const RenderPsychographicSummary = ({ summary, polarization }) => {
    if (!summary) return null;

    const metrics = summary || {};
    const polEngage = (metrics.political_profile?.political_engagement || 0) * 100;
    
    // Calculate derived insights
    const moralFoundationStrength = [
        metrics.mft_care_harm,
        metrics.mft_fairness_cheating,
        metrics.mft_loyalty_betrayal,
        metrics.mft_authority_subversion,
        metrics.mft_sanctity_degradation
    ].reduce((a, b) => (a || 0) + (b || 0), 0) / 5;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-indigo-500/20 rounded-lg border border-indigo-500/30">
                    <Brain size={20} className="text-indigo-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white">Audience Neuro-Profile</h2>
                    <p className="text-xs text-slate-400">Cognitive architecture and demographic signals.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                
                {/* --- COLUMN 1: NEURO-COGNITIVE DRIVERS (Left) --- */}
                <div className="lg:col-span-4 flex flex-col gap-4">
                    {/* Moral Foundations Card */}
                    <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full -mr-10 -mt-10 pointer-events-none"></div>
                        
                        <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                            <Activity size={16} className="text-indigo-400"/> Moral Foundations
                        </h4>

                        <div className="space-y-3 mb-4">
                            <ModernProgressBar label="Care / Compassion" score={(metrics.mft_care_harm || 0) * 100} colorClass="bg-gradient-to-r from-pink-500 to-rose-400" />
                            <ModernProgressBar label="Fairness / Justice" score={(metrics.mft_fairness_cheating || 0) * 100} colorClass="bg-gradient-to-r from-blue-400 to-cyan-400" />
                            <ModernProgressBar label="Loyalty / Tradition" score={(metrics.mft_loyalty_betrayal || 0) * 100} colorClass="bg-gradient-to-r from-amber-400 to-orange-400" />
                            <ModernProgressBar label="Authority / Order" score={(metrics.mft_authority_subversion || 0) * 100} colorClass="bg-gradient-to-r from-purple-400 to-violet-400" />
                            <ModernProgressBar label="Sanctity / Purity" score={(metrics.mft_sanctity_degradation || 0) * 100} colorClass="bg-gradient-to-r from-emerald-400 to-teal-400" />
                        </div>

                        {/* Moral Foundation Strength Indicator */}
                        <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/30">
                            <div className="text-xs text-slate-500 mb-1">Foundation Activation</div>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full transition-all duration-1000 ${
                                            moralFoundationStrength > 0.6 ? 'bg-emerald-500' : 
                                            moralFoundationStrength > 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${moralFoundationStrength * 100}%` }}
                                    />
                                </div>
                                <span className="text-sm font-mono font-bold text-white">
                                    {(moralFoundationStrength * 100).toFixed(0)}%
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Personality Traits (OCEAN) */}
                    <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-5">
                        <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                            <Users size={16} className="text-purple-400"/> Big Five Personality
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                            <ScorePill label="Openness" value={`${((metrics.ocean_openness || 0) * 100).toFixed(0)}%`} icon={Lightbulb} color="text-yellow-400" />
                            <ScorePill label="Conscientious" value={`${((metrics.ocean_conscientiousness || 0) * 100).toFixed(0)}%`} icon={ListChecks} color="text-emerald-400" />
                            <ScorePill label="Extraversion" value={`${((metrics.ocean_extraversion || 0) * 100).toFixed(0)}%`} icon={Users} color="text-pink-400" />
                            <ScorePill label="Agreeable" value={`${((metrics.ocean_agreeableness || 0) * 100).toFixed(0)}%`} icon={Heart} color="text-cyan-400" />
                            <ScorePill label="Neuroticism" value={`${((metrics.ocean_neuroticism || 0) * 100).toFixed(0)}%`} icon={AlertTriangle} color="text-red-400" />
                        </div>
                    </div>

                    {/* Cognitive Style Indicators */}
                    {(metrics.need_cognitive_closure || metrics.epistemic_motivation || metrics.regulatory_focus) && (
                        <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-5">
                            <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                                <Brain size={16} className="text-cyan-400"/> Cognitive Style
                            </h4>
                            <div className="space-y-3">
                                {metrics.need_cognitive_closure && (
                                    <ModernProgressBar 
                                        label="Need for Closure" 
                                        score={(metrics.need_cognitive_closure || 0) * 100} 
                                        colorClass="bg-gradient-to-r from-blue-500 to-indigo-500" 
                                    />
                                )}
                                {metrics.epistemic_motivation && (
                                    <ModernProgressBar 
                                        label="Info Seeking" 
                                        score={(metrics.epistemic_motivation || 0) * 100} 
                                        colorClass="bg-gradient-to-r from-purple-500 to-pink-500" 
                                    />
                                )}
                                {metrics.regulatory_focus && (
                                    <ModernProgressBar 
                                        label="Regulatory Focus" 
                                        score={(metrics.regulatory_focus || 0) * 100} 
                                        colorClass="bg-gradient-to-r from-amber-500 to-orange-500" 
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* --- COLUMN 2: STRATEGIC SYNTHESIS (Middle) --- */}
                <div className="lg:col-span-4 flex flex-col h-full">
                    <SynthesizedInsights 
                        metrics={summary} 
                        polarization={polarization}
                        insights={summary.synthesized_insights} 
                    />
                </div>

                {/* --- COLUMN 3: AUDIENCE SNAPSHOT (Right) --- */}
                <div className="lg:col-span-4 flex flex-col gap-4">
                    {/* Demographics */}
                    <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-500/10 blur-3xl rounded-full -ml-10 -mb-10 pointer-events-none"></div>

                        <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                            <Users size={16} className="text-teal-400"/> Audience Snapshot
                        </h4>
                        
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="p-3 bg-slate-900/40 rounded-lg border border-slate-700/30">
                                <span className="text-xs text-slate-400 mb-1 flex items-center gap-1.5"><Clock size={12}/> Avg. Age</span>
                                <span className="text-2xl font-black font-mono text-white">{(metrics.target_age || 0).toFixed(1)}</span>
                            </div>
                            <div className="p-3 bg-slate-900/40 rounded-lg border border-slate-700/30">
                                <span className="text-xs text-slate-400 mb-1 flex items-center gap-1.5"><MapPin size={12}/> Setting</span>
                                <span className="text-lg font-bold text-white capitalize truncate">{toTitleCase(metrics.dominant_location_type || "Urban")}</span>
                            </div>
                        </div>

                        {/* Political Engagement Gauge */}
                        <div className="p-3 bg-slate-900/40 rounded-lg border border-slate-700/30 mb-4">
                            <div className="flex items-center gap-4">
                                <div className="relative w-16 h-16 flex-shrink-0">
                                    <svg className="w-full h-full transform -rotate-90">
                                        <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-slate-800" />
                                        <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="transparent" strokeDasharray={2 * Math.PI * 28} strokeDashoffset={2 * Math.PI * 28 * (1 - polEngage / 100)} className={`${polEngage > 70 ? 'text-emerald-500' : 'text-blue-500'} transition-all duration-1000`} />
                                    </svg>
                                    <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">{polEngage.toFixed(0)}%</span>
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-white">Political Engagement</div>
                                    <div className="text-xs text-slate-400 mt-1">Turnout & content interaction likelihood</div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Demographics */}
                        {metrics.socioeconomic_status && (
                            <div className="bg-slate-900/40 p-3 rounded-lg border border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-2">Socioeconomic Profile</div>
                                <div className="text-sm font-bold text-white capitalize">
                                    {toTitleCase(metrics.socioeconomic_status)}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Ideological Profile */}
                    {metrics.political_profile && (
                        <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-5">
                            <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                                <Target size={16} className="text-rose-400"/> Ideological Drivers
                            </h4>
                            <div className="space-y-3">
                                {metrics.political_profile.authoritarianism !== undefined && (
                                    <ModernProgressBar 
                                        label="Authoritarianism" 
                                        score={(metrics.political_profile.authoritarianism || 0) * 100} 
                                        colorClass="bg-gradient-to-r from-red-500 to-orange-500" 
                                    />
                                )}
                                {metrics.political_profile.social_dominance_orientation !== undefined && (
                                    <ModernProgressBar 
                                        label="Hierarchy Preference" 
                                        score={(metrics.political_profile.social_dominance_orientation || 0) * 100} 
                                        colorClass="bg-gradient-to-r from-purple-500 to-violet-500" 
                                    />
                                )}
                                {metrics.threat_sensitivity !== undefined && (
                                    <ModernProgressBar 
                                        label="Threat Sensitivity" 
                                        score={(metrics.threat_sensitivity || 0) * 100} 
                                        colorClass="bg-gradient-to-r from-amber-500 to-red-500" 
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Values & Orientations */}
                    {(metrics.future_orientation || metrics.rwa_score || metrics.individualism_collectivism) && (
                        <div className="bg-slate-800/60 backdrop-blur-md border border-slate-700/50 rounded-xl p-5">
                            <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                                <Compass size={16} className="text-indigo-400"/> Value Orientations
                            </h4>
                            <div className="grid grid-cols-2 gap-3">
                                {metrics.future_orientation !== undefined && (
                                    <ScorePill 
                                        label="Future Focus" 
                                        value={`${((metrics.future_orientation || 0) * 100).toFixed(0)}%`} 
                                        icon={TrendingUp} 
                                        color="text-cyan-400" 
                                    />
                                )}
                                {metrics.rwa_score !== undefined && (
                                    <ScorePill 
                                        label="Traditionalism" 
                                        value={`${((metrics.rwa_score || 0) * 100).toFixed(0)}%`} 
                                        icon={Shield} 
                                        color="text-amber-400" 
                                    />
                                )}
                                {metrics.individualism_collectivism !== undefined && (
                                    <ScorePill 
                                        label={metrics.individualism_collectivism > 0.5 ? "Individualist" : "Collectivist"} 
                                        value={`${((metrics.individualism_collectivism || 0) * 100).toFixed(0)}%`} 
                                        icon={Users} 
                                        color="text-purple-400" 
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- 2. Center Column Helper (The "So What?") ---

export const SynthesizedInsights = ({ metrics, insights, polarization }) => { 
    // Frontend Synthesis Logic
    const synthesized = useMemo(() => {
        if (insights) return insights;

        const overview = polarization?.rationale || 
            `Primary audience segment shows high responsiveness to competence cues.`;

        // Safe Defaults
        let motivators = ["Clear, actionable solutions"];
        let turn_offs = ["Visual clutter"];
        let style = "Professional and direct.";

        if ((metrics.rwa_score || 0) > 0.5) {
            motivators.push("Tradition & Stability");
            turn_offs.push("Radical imagery");
        }
        if ((metrics.mft_care_harm || 0) > 0.6) {
            motivators.push("Community Welfare");
            style = "Warm, empathetic, and people-focused.";
        }

        return { overview, motivators, turn_offs, communication_style: style };
    }, [metrics, insights, polarization]);

    return (
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-md border border-slate-700/50 rounded-xl p-5 h-full flex flex-col">
            
            <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-amber-400" />
                <h4 className="text-sm font-bold text-white">Strategic Synthesis</h4>
            </div>

            <div className="flex-1 space-y-4">
                <p className="text-sm text-slate-300 leading-relaxed italic border-l-2 border-amber-500/50 pl-4 py-1">
                    "{synthesized.overview}"
                </p>

                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/30">
                        <h5 className="text-[10px] font-bold text-teal-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><ThumbsUp size={12} /> Key Motivators</h5>
                        <ul className="space-y-1.5">
                            {synthesized.motivators.slice(0,3).map((item, i) => (
                                <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                                    <span className="text-teal-500 mt-0.5">•</span> {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700/30">
                        <h5 className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><TrendingDown size={12} /> Key Turn-offs</h5>
                        <ul className="space-y-1.5">
                            {synthesized.turn_offs.slice(0,3).map((item, i) => (
                                <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                                    <span className="text-red-500 mt-0.5">•</span> {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="bg-slate-900/30 p-3 rounded-lg border border-white/5 mt-auto">
                    <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5"><MessageSquare size={12} /> Creative Direction</h5>
                    <p className="text-xs text-white leading-relaxed">{synthesized.communication_style}</p>
                </div>
            </div>
        </div>
    );
};


// --- 3. Enhanced Recommendation Card (Cinematic Style) ---

export const NewImageCard = ({ asset, onClick }) => {
    const [imageUrl, setImageUrl] = useState(null);

    useEffect(() => {
        let active = true;
        const fetchUrl = async () => {
            // Handle both root and nested paths
            const path = asset.multimodal_coherence_analysis?.object_path || asset.object_path;
            if (path) {
                try {
                    const url = await getStorageURL(path);
                    if (active) { setImageUrl(url); return; }
                } catch (e) { console.warn("SDK load failed", e); }
            }
            if (active && (asset.displayable_url || asset.composition_analysis?.detailed_medium_metrics?.displayable_url)) {
                 setImageUrl(asset.displayable_url || asset.composition_analysis?.detailed_medium_metrics?.displayable_url);
            }
        };
        fetchUrl();
        return () => { active = false; };
    }, [asset]);

    // Data Accessor Logic
    const fitScore = ((asset.psychological_drivers_analysis?.strategic_fit_score ?? asset.strategic_fit_score) || 0) * 100;
    const scoreColor = fitScore > 80 ? "text-emerald-400" : fitScore > 60 ? "text-yellow-400" : "text-red-400";
    const ringColor = fitScore > 80 ? "border-emerald-500" : fitScore > 60 ? "border-yellow-500" : "border-red-500";
    
    const rank = asset.psychological_drivers_analysis?.rank ?? asset.rank;
    const caption = asset.psychological_drivers_analysis?.suggested_caption ?? asset.suggested_caption;
    const aestheticScore = (asset.aesthetic_quality_score || 0) * 100;
    const narrativeScore = (asset.narrative_structure?.narrative_score || 0) * 100;
    const attentionScore = (asset.attention_capture_analysis?.attention_capture_score || 0) * 100;
    
    // Phase & Emotion
    const phase = asset.multimodal_coherence_analysis?.optimal_campaign_phase ?? asset.optimal_campaign_phase;
    const emotion = asset.dominant_emotion;

    return (
        <div 
            className="flex-shrink-0 w-72 bg-slate-900/40 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden group cursor-pointer hover:border-slate-500 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-900/20 hover:-translate-y-1"
            onClick={() => onClick(asset)}
        >
            {/* Image Section */}
            <div className="relative h-64 bg-slate-950">
                {imageUrl ? (
                    <img src={imageUrl} alt="Asset" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-700"><ImageIcon size={32} /></div>
                )}
                
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent opacity-90"></div>

                {/* Top Badges */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className="bg-white/10 backdrop-blur-md border border-white/10 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-lg">
                        #{rank}
                    </span>
                    {asset.viral_potential_analysis?.shareability_category === 'HIGH' && (
                        <span className="bg-purple-500/80 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                            <Zap size={10} /> Viral
                        </span>
                    )}
                    {asset.authenticity_differentiation?.category === 'HIGHLY_AUTHENTIC' && (
                        <span className="bg-emerald-500/80 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-lg flex items-center gap-1">
                            <Award size={10} /> Authentic
                        </span>
                    )}
                </div>

                {/* Bottom Info */}
                <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex justify-between items-end mb-2">
                         <div>
                            <span className="text-[10px] uppercase text-slate-400 font-bold tracking-wider block mb-0.5">Strategic Fit</span>
                            <span className={`text-2xl font-black font-mono ${scoreColor}`}>{fitScore.toFixed(0)}%</span>
                         </div>
                         <div className="text-right">
                             <div className={`w-10 h-10 rounded-full border-2 ${ringColor} flex items-center justify-center bg-slate-900/50 backdrop-blur-md`}>
                                 <Sparkles size={18} className="text-white" />
                             </div>
                         </div>
                    </div>
                    <p className="text-xs text-slate-200 line-clamp-2 font-medium leading-relaxed">
                        {caption || "No caption generated."}
                    </p>
                </div>
            </div>

            {/* Enhanced Metrics Footer */}
            <div className="p-3 bg-slate-900/50 border-t border-slate-700/50">
                {/* Primary Metrics Row */}
                <div className="grid grid-cols-3 gap-2 text-[10px] mb-2">
                    <div className="flex flex-col items-center text-center">
                        <Palette size={12} className="text-fuchsia-400 mb-1"/>
                        <span className="text-slate-200 font-mono font-bold">{aestheticScore.toFixed(0)}%</span>
                        <span className="text-slate-500">Aesthetic</span>
                    </div>
                    <div className="flex flex-col items-center text-center">
                        <MessageSquare size={12} className="text-blue-400 mb-1"/>
                        <span className="text-slate-200 font-mono font-bold">{narrativeScore.toFixed(0)}%</span>
                        <span className="text-slate-500">Narrative</span>
                    </div>
                    <div className="flex flex-col items-center text-center">
                        <Eye size={12} className="text-amber-400 mb-1"/>
                        <span className="text-slate-200 font-mono font-bold">{attentionScore.toFixed(0)}%</span>
                        <span className="text-slate-500">Attention</span>
                    </div>
                </div>
                
                {/* Secondary Info Row */}
                <div className="flex items-center justify-between text-[10px] pt-2 border-t border-slate-700/30">
                    <div className="flex items-center gap-1.5 text-slate-400">
                        <Target size={12} />
                        <span className="truncate">{toTitleCase(phase?.split('_')[0] || "General")}</span>
                    </div>
                    {emotion && (
                        <div className="flex items-center gap-1.5 text-slate-400">
                            <Heart size={12} />
                            <span className="truncate capitalize">{emotion}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const RenderRankedRecommendations = ({ list, onImageClick }) => {
    return (
        <div className="pb-8">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/20 rounded-lg border border-emerald-500/30">
                        <ImageIcon size={20} className="text-emerald-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white">Top Recommendations</h2>
                        <p className="text-xs text-slate-400">Prioritized by strategic fit and resonance.</p>
                    </div>
                </div>
            </div>

            {list && list.length > 0 ? (
                <div className="flex overflow-x-auto gap-5 pb-6 pt-2 custom-scrollbar px-1">
                    {list.map((asset, i) => (
                        <NewImageCard key={asset.gcs_path || i} asset={asset} onClick={onImageClick} />
                    ))}
                </div>
            ) : (
                <div className="p-8 border border-dashed border-slate-700 rounded-xl bg-slate-800/20 text-center">
                    <p className="text-slate-500">No recommendations available.</p>
                </div>
            )}
        </div>
    );
};


// --- 4. Strategic Gaps (REPLACED) ---

export const RenderStrategicGaps = ({ list }) => {
    if (!list || list.length === 0) return null;

    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-rose-500/20 rounded-lg border border-rose-500/30">
                    <Lightbulb size={20} className="text-rose-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white">Strategic Content Gaps</h2>
                    <p className="text-xs text-slate-400">High-priority unmet visual narratives</p>
                </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {list.map((gap, index) => (
                    <div key={index} className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 border border-rose-500/20 rounded-xl p-5 hover:border-rose-500/40 transition-all duration-300 group relative overflow-hidden">
                        {/* Ambient glow */}
                        <div className="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 blur-2xl rounded-full -mr-10 -mt-10 pointer-events-none group-hover:bg-rose-500/10 transition-all duration-300"></div>
                        
                        <div className="relative z-10">
                            {/* Header */}
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-rose-500/10 rounded-lg border border-rose-500/20 group-hover:bg-rose-500/20 transition-colors">
                                    <AlertTriangle size={18} className="text-rose-400" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-base font-bold text-white group-hover:text-rose-200 transition-colors leading-tight">
                                        {gap.theme_name}
                                    </h4>
                                    {gap.priority_score && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-slate-500">Priority:</span>
                                            <div className="flex-1 bg-slate-700/50 rounded-full h-1.5 overflow-hidden max-w-[100px]">
                                                <div 
                                                    className="h-full bg-rose-500 rounded-full transition-all duration-700"
                                                    style={{ width: `${gap.priority_score * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs font-mono text-rose-400">
                                                {Math.round(gap.priority_score * 100)}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Quote Section */}
                            <div className="mb-4 bg-slate-900/60 p-4 rounded-lg border border-slate-700/30 relative">
                                <Quote size={14} className="absolute top-3 left-3 text-slate-600" />
                                <p className="text-sm text-slate-200 italic pl-6 leading-relaxed">
                                    "{gap.base_ovid_quote}"
                                </p>
                            </div>

                            {/* AI Recommendation */}
                            <div className="space-y-3">
                                <div className="flex items-start gap-2">
                                    <Camera size={16} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                        <span className="text-xs uppercase text-cyan-400 font-bold tracking-wider block mb-1">
                                            Visual Recommendation
                                        </span>
                                        <p className="text-sm text-slate-300 leading-snug">
                                            {gap.ai_recommendation}
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Expected Impact Metrics */}
                                {gap.expected_impact && (
                                    <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-700/30">
                                        <div className="bg-slate-800/50 p-2 rounded">
                                            <div className="text-xs text-slate-500 mb-1">Archetype Alignment</div>
                                            <div className="text-sm font-bold text-white">
                                                {gap.expected_impact.archetype_alignment || 'N/A'}
                                            </div>
                                        </div>
                                        <div className="bg-slate-800/50 p-2 rounded">
                                            <div className="text-xs text-slate-500 mb-1">Emotional Trigger</div>
                                            <div className="text-sm font-bold text-white">
                                                {gap.expected_impact.emotional_trigger || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const AuditCard = ({ icon: Icon, title, score, label, summary, color }) => {
    const colorMap = {
        'blue': { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
        'purple': { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
        'teal': { text: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/20' },
        'accent': { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
        'red': { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
        'amber': { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    };
    
    const colorKey = color?.includes('-') ? color.split('-')[1] : (color || 'accent');
    const theme = colorMap[colorKey] || colorMap['accent'];

    return (
        <div className={`flex-shrink-0 w-80 bg-slate-800/40 backdrop-blur-sm rounded-xl border ${theme.border} p-5 flex flex-col justify-between`}>
            <div>
                <div className="flex justify-between items-start mb-4">
                    <div className={`p-2 rounded-lg ${theme.bg}`}>
                        <Icon size={20} className={theme.text} />
                    </div>
                    <span className={`text-2xl font-black font-mono ${theme.text}`}>{score}</span>
                </div>
                
                <h4 className="text-sm font-bold text-white mb-1">{title}</h4>
                <div className={`text-[10px] uppercase font-bold tracking-wider opacity-70 mb-3 ${theme.text}`}>{label}</div>
                
                <p className="text-xs text-slate-400 leading-relaxed border-t border-white/5 pt-3">
                    {summary}
                </p>
            </div>
        </div>
    );
};

export const RenderCampaignAudits = ({ brand, messaging, demographics, psychographics }) => {
    const metrics = psychographics || {};
    
    const cognitiveScore = ((metrics.ocean_conscientiousness || 0.5) + (metrics.need_cognitive_closure || 0.5)) / 2;
    const cogLabel = cognitiveScore > 0.6 ? "Low" : "High";
    
    const futureScore = metrics.future_orientation || 0.5;
    const isVisionary = futureScore > 0.55;

    const threatScore = metrics.threat_sensitivity || 0.5;
    const isThreatSensitive = threatScore > 0.6;

    return (
        <div className="pb-8">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                    <ListChecks size={20} className="text-blue-400" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-white">Strategic Audits</h2>
                    <p className="text-xs text-slate-400">System-wide coherence and capability checks.</p>
                </div>
            </div>

            <div className="flex overflow-x-auto gap-5 pb-4 custom-scrollbar">
                
                <AuditCard 
                    icon={Activity}
                    title="Cognitive Preference"
                    score={cogLabel}
                    label="Preferred Complexity"
                    summary={cogLabel === "Low" ? "Audience prefers clear binaries and simple messaging." : "Audience tolerates nuance and complexity."}
                    color="blue"
                />

                <AuditCard 
                    icon={Clock}
                    title="Time Horizon"
                    score={isVisionary ? "Future" : "Past"}
                    label="Temporal Focus"
                    summary={isVisionary ? "Driven by innovation and 'what's next'." : "Driven by heritage, restoration, and safety."}
                    color={isVisionary ? "purple" : "amber"}
                />

                <AuditCard 
                    icon={Zap}
                    title="Neuro-Sensitivity"
                    score={isThreatSensitive ? "High" : "Low"}
                    label="Threat Response"
                    summary={isThreatSensitive ? "Avoid aggressive palettes. Use calming tones." : "Open to bold, high-contrast aesthetics."}
                    color={isThreatSensitive ? "red" : "teal"}
                />

                {brand && (
                    <AuditCard 
                        icon={Palette} 
                        title="Visual Consistency" 
                        score={(brand.consistency_score * 100).toFixed(0) + "%"}
                        label="Coherence Score"
                        summary={brand.recommendation}
                        color="purple"
                    />
                )}

                {messaging && (
                    <AuditCard 
                        icon={MessageSquare} 
                        title="Message Clarity" 
                        score={(messaging.overall_consistency_score * 100).toFixed(0) + "%"}
                        label="Thematic Alignment"
                        summary={messaging.recommendation}
                        color="accent"
                    />
                )}
            </div>
        </div>
    );
};

